import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CarePlanService } from 'src/app/services/careplan.service';
import { StaffService } from 'src/app/services/staff.service';
import { ClientService } from 'src/app/services/client.service';
import { CarePlan, ServiceDetail } from 'src/app/models/careplan.model';
import { ServicePlanDetail, ServiceDetail as ServicePlanServiceDetail } from 'src/app/models/serviceplandetail.model';
import { TaskItem } from 'src/app/models/task.model';
import { Options } from '@angular-slider/ngx-slider';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { ProgressDialogComponent } from '../progress-dialog/progress-dialog.component';
import { EventBusService } from 'src/app/services/event-bus.service';
import { NotificationService } from 'src/app/services/notification.service';
import { stampAllocationOrigin } from 'src/app/services/allocation-capture';

@Component({
  selector: 'app-care-plan-dialog',
  templateUrl: './care-plan-dialog.component.html',
  styleUrls: ['../../templates/dialog-template.css', './care-plan-dialog.component.css']
})
export class CarePlanDialogComponent implements OnInit {
  carePlan: CarePlan | null = null;
  servicePlanDetail: ServicePlanDetail | null = null;
  loading = true;
  error: string | null = null;
  selectedService: ServiceDetail | null = null;
  selectedServiceDetails: ServicePlanServiceDetail | null = null;
  initialStartTime: string = '';
  initialEndTime: string = '';
  timeChanged: boolean = true;
  rateWindow: { start_time: string, end_time: string } | null = null;
  rateWindowError: string | null = null;
  taskItem: TaskItem;
  staffLabel: string | undefined;
  private progressDialogRef: MatDialogRef<ProgressDialogComponent> | null = null;
  profilePhotoUrl: string | null = null;
  loadingPhoto = true;
  
  // Two-person assist properties
  is2pa = false;
  hasMultipleCarers = false;
  allocatedCarers: { id: string, name: string }[] = [];
  originalCarerString = '';
  pendingChanges = false;

  sliderValue: number = 0;
  endValue: number = 0;
  sliderOptions: Options = {
    floor: 0,
    ceil: 1440,
    step: 5,
  };

  constructor(
    public dialogRef: MatDialogRef<CarePlanDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { event: TaskItem },
    private carePlanService: CarePlanService,
    private staffService: StaffService,
    private eventBusService: EventBusService, private notificationService: NotificationService,
    private clientService: ClientService,
    private dialog: MatDialog
  ) {
    this.taskItem = data.event;
  }

  ngOnInit() {
    if (this.taskItem.carer_string) {
      this.staffLabel = this.staffService.getStaffLabel(this.taskItem.carer_string);
    }
    this.loadCarePlan();
    this.loadProfilePhoto();
    this.initializeTwoPersonAssistData();
  }
  
  private initializeTwoPersonAssistData() {
    // Check if this is a two-person assist event
    this.is2pa = this.taskItem.is_2pa === true;
    
    // Store the original carer string for comparison
    this.originalCarerString = this.taskItem.carer_string?.toString() || '';
    
    // Check if this event has multiple carers allocated
    this.hasMultipleCarers = this.originalCarerString.includes(',');
    
    // Only process if this is a 2PA with multiple carers
    if (this.is2pa && this.hasMultipleCarers) {
      // Split the carer string into individual carer IDs
      const carerIds = this.originalCarerString.split(',').map(id => id.trim());
      
      // Get the name for each carer ID
      this.allocatedCarers = carerIds.map(id => {
        const name = this.staffService.getStaffLabel(id) || 'Unknown Staff';
        return { id, name };
      });
    }
  }
  
  removeCarer(carerId: string) {
    // Find the index of the carer to remove
    const index = this.allocatedCarers.findIndex(carer => carer.id === carerId);
    
    // Only proceed if the carer was found and we have more than one carer
    if (index !== -1 && this.allocatedCarers.length > 1) {
      // Remove the carer from the array
      this.allocatedCarers.splice(index, 1);
      
      // Mark that we have pending changes
      this.pendingChanges = true;
    }
  }
  
  applyChanges() {
    // Only proceed if we have pending changes
    if (!this.pendingChanges) {
      this.onClose();
      return;
    }
    
    // Create the new carer string from the remaining allocated carers
    const newCarerString = this.allocatedCarers.map(carer => carer.id).join(',');
    
    // Update the task item with the new carer string
    this.taskItem.carer_string = newCarerString;
    
    // Update the partially_allocated flag based on the number of carers
    this.taskItem.partially_allocated = this.allocatedCarers.length === 1;
    
    // Mark the event as having a pending allocation change
    this.taskItem.pending_allocation_change = true;
    this.taskItem.pending_change = true;
    // ML action log (capture v2): only assigns the item's log_* fields
    stampAllocationOrigin(this.taskItem, 'dialog');
    
    // Close the dialog and return the updated task item
    this.dialogRef.close({ 
      success: true, 
      event: this.taskItem 
    });
  }

  private loadProfilePhoto() {
    this.loadingPhoto = true;
    // Only attempt to load photo if this is not a staff event and residentID exists
    if (this.taskItem.residentID) {
      this.clientService.getClientPhoto(this.taskItem.residentID).subscribe({
        next: (blob) => {
          this.profilePhotoUrl = URL.createObjectURL(blob);
          this.loadingPhoto = false;
        },
        error: (error) => {
          this.loadingPhoto = false;
          this.profilePhotoUrl = null;
        }
      });
    } else {
      this.loadingPhoto = false;
      this.profilePhotoUrl = null;
    }
  }

  // "Set Current to Key" REMOVED (eCase #6542, 2026-09): writing key staff through
  // carehub-api made eCase insert duplicate blank GroupActivityManagementList rows
  // that block plan deactivation and resident movement-out. The API endpoint is also
  // disabled. Key-staff editing is to be rebuilt in keystone / the scheduler.

  private loadCarePlan() {
    // Skip loading care plan for staff events without clients
    if (!this.taskItem.residentID) {
      this.error = 'Care plan not available for staff events';
      this.loading = false;
      return;
    }
    
    this.carePlanService.getClientCarePlan(this.taskItem.residentID)
      .subscribe({
        next: (response) => {
          this.carePlan = response;
          this.findSelectedService();
          this.loading = false;
        },
        error: (error) => {
          this.error = 'Failed to load care plan';
          this.loading = false;
          this.notificationService.addMessage('critical', 'Error loading care plan: ' + error, error);
        }
      });
  }

  private findSelectedService() {
    if (this.carePlan) {
      this.selectedService = this.carePlan.servicesDetails.find(service =>
        service.serviceFundingSources.some(source => source.CCQuoteDetailID === this.taskItem.CCQuoteDetailID)
      ) || null;

      if (this.selectedService) {
        this.initialStartTime = this.selectedService.StartTime;
        this.initialEndTime = this.formatTimeLabel(this.timeStringToMinutes(this.selectedService.StartTime) + Number(this.selectedService.Duration));
        this.loadRateWindow();
      }
    }
  }

  private loadRateWindow() {
    if (this.selectedService) {
      this.carePlanService.getRateWindow(this.selectedService)
        .subscribe({
          next: (response) => {
            if (response.message === 'ok' && response.start_time && response.end_time) {
              // Success: valid rate window returned
              this.rateWindow = { start_time: response.start_time, end_time: response.end_time };
              this.rateWindowError = null;
              this.initializeSlider();
            } else {
              // Error: no valid rate window found
              this.rateWindow = null;
              this.rateWindowError = response.message || 'Unable to determine rate window';
            }
          },
          error: (error) => {
            // Network or other API error
            this.rateWindow = null;
            this.rateWindowError = 'Failed to load rate window information';
            this.notificationService.addMessage('warning', 'Failed to load rate window', error);
          }
        });
    }
  }

  private initializeSlider() {
    if (this.rateWindow) {
      let rateWindowStart = this.timeStringToMinutes(this.rateWindow.start_time);
      let rateWindowEnd = this.timeStringToMinutes(this.rateWindow.end_time);

      if (rateWindowEnd <= rateWindowStart) {
        rateWindowEnd += 1440;
      }

      const serviceDuration = Number(this.selectedService!.Duration);

      this.sliderOptions = {
        floor: rateWindowStart,
        ceil: rateWindowEnd,
        step: 5,
        showTicks: true,
        tickStep: 60,
        showTicksValues: false,
        ticksArray: this.generateTimeTicks(rateWindowStart, rateWindowEnd),
        translate: (value: number): string => {
          return this.formatTimeLabel(value);
        },
        disabled: false,
        draggableRangeOnly: true,
      } as Options;

      let currentMinutes = this.timeStringToMinutes(this.selectedService!.StartTime);

      if (currentMinutes < rateWindowStart) {
        currentMinutes += 1440;
      }

      this.sliderValue = (currentMinutes >= rateWindowStart && currentMinutes <= rateWindowEnd)
        ? Math.round(currentMinutes / 5) * 5
        : rateWindowStart;

      this.endValue = this.sliderValue + serviceDuration;
    }
  }

  // onSliderChange() removed (eCase #6542): permanent service-time editing disabled.

  private timeStringToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private minutesToTimeString(minutes: number): string {
    const adjustedMinutes = minutes % 1440;
    const hours = Math.floor(adjustedMinutes / 60);
    const mins = adjustedMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  private generateTimeTicks(start: number, end: number): number[] {
    const ticks: number[] = [];
    for (let minute = start; minute <= end; minute += 60) {
      ticks.push(minute);
    }
    return ticks;
  }  

  formatTimeLabel(minutes: number): string {
    const adjustedMinutes = minutes % 1440;
    const hours = Math.floor(adjustedMinutes / 60);
    const mins = adjustedMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  // onPublish() removed (eCase #6542): permanently changing a service time drove
  // careplan/timing -> updateServicePlanDetails, which created duplicate eCase
  // group-activity records. Make permanent service-time changes directly in eCase.

  onClose(): void {
    if (this.profilePhotoUrl) {
      URL.revokeObjectURL(this.profilePhotoUrl);
    }
    this.dialogRef.close();
  }
}
